/*
 * ConfirmCard — Yes/No confirmation directive.
 *
 * The agent calls `render(directive_type='confirm', payload={yes_label,
 * no_label, action})` when it's proposing a specific action that needs
 * an explicit OK before firing (e.g. "Should I scan brndsh.io?").
 *
 * Brand: brand-50 surface card, 1px ring, no left-edge color stripe.
 * Two button row — primary Yes (brand-blue), secondary No (gray).
 * Plex Sans, sentence case, periods. No emoji.
 */

import React from 'react';
import { Check, X } from 'lucide-react';

import type { DirectivePayload } from '../../lib/dossier-types';


export interface ConfirmCardProps {
  payload: DirectivePayload;
  /** Fires with the action key the agent supplied (yes path) OR
   *  `${action}_no` when the user declines. The chat handler routes the
   *  value back through the same chip-select path so the next /converse
   *  turn sees what was picked. */
  onAction: (value: string, label: string) => void;
  disabled?: boolean;
}


export const ConfirmCard: React.FC<ConfirmCardProps> = ({ payload, onAction, disabled }) => {
  const yesLabel = payload.yes_label || 'Yes';
  const noLabel = payload.no_label || 'No';
  const action = payload.action || 'confirm';

  return (
    <div className="aa-directive aa-directive--confirm">
      <div className="aa-directive-actions">
        <button
          type="button"
          className="aa-directive-btn aa-directive-btn--primary"
          disabled={disabled}
          onClick={() => onAction(`__${action}_yes`, yesLabel)}
        >
          <Check size={13} strokeWidth={2.5} />
          <span>{yesLabel}</span>
        </button>
        <button
          type="button"
          className="aa-directive-btn aa-directive-btn--ghost"
          disabled={disabled}
          onClick={() => onAction(`__${action}_no`, noLabel)}
        >
          <X size={13} strokeWidth={2.5} />
          <span>{noLabel}</span>
        </button>
      </div>
    </div>
  );
};

export default ConfirmCard;
